import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Link } from '@mui/material';
import { Close as CloseIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { firstStepsApi, FirstStepKey, FirstSteps } from '../../services/firstStepsApi';
import { BOT_URL } from '../../config/telegram';
import { tokens } from '../../theme/tokens';
import { StepList } from './StepList';

interface Props {
  /** What Início shows instead, once the list is done or hidden. */
  fallback?: React.ReactNode;
}

const hiddenKey = (userId: string, what: 'list' | 'goal') => `financy_first_steps_hidden:${what}:${userId}`;

const readHidden = (userId: string | undefined, what: 'list' | 'goal'): boolean => {
  if (!userId) return false;
  try {
    return localStorage.getItem(hiddenKey(userId, what)) === '1';
  } catch {
    return false;
  }
};

const rememberHidden = (userId: string, what: 'list' | 'goal') => {
  try {
    localStorage.setItem(hiddenKey(userId, what), '1');
  } catch {
    // Private mode: it shows again next time, nothing worse.
  }
};

/**
 * "Primeiros passos" on the personal Início: five steps, each with the way
 * to do it, ticked off by what the person actually recorded, here or by Fin
 * (coming back to the tab looks again). Done, it gives way to one suggestion,
 * a goal, and then to Início itself.
 */
const FirstStepsCard: React.FC<Props> = ({ fallback = null }) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { state } = useAuth();
  const user = state.user;
  const [data, setData] = useState<FirstSteps | null>(null);
  const [failed, setFailed] = useState(false);
  const [hidden, setHidden] = useState(() => readHidden(user?.id, 'list'));
  const [goalHidden, setGoalHidden] = useState(() => readHidden(user?.id, 'goal'));

  const load = useCallback(async () => {
    try {
      setData(await firstStepsApi.get());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    if (hidden && goalHidden) return undefined;
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [load, hidden, goalHidden]);

  if (!user || failed || hidden) return <>{fallback}</>;
  if (!data) return null;

  const done = data.steps.filter((step) => step.doneAt).length;
  const total = data.steps.length;
  const telegramLinked = Boolean(user.isTelegramLinked);

  if (done === total) {
    if (data.goalAt || goalHidden) return <>{fallback}</>;
    return (
      <Box
        component='section'
        aria-label={t('firstSteps.goal.title')}
        sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, p: { xs: 2.25, md: 2.75 }, pr: { xs: 6, md: 7 }, borderRadius: `${tokens.radius.card}px`, bgcolor: tokens.accentSoft }}
      >
        <Box sx={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 700 }}>{t('firstSteps.goal.title')}</Box>
          <Box component='span' sx={{ color: tokens.ink2, fontSize: 14 }}>{t('firstSteps.goal.text')}</Box>
        </Box>
        <Button variant='contained' onClick={() => navigate('/planning', { state: { open: 'goal' } })} sx={{ minHeight: 44 }}>
          {t('firstSteps.goal.action')}
        </Button>
        <IconButton
          aria-label={t('firstSteps.goal.close')}
          onClick={() => {
            rememberHidden(user.id, 'goal');
            setGoalHidden(true);
          }}
          sx={{ position: 'absolute', top: 8, right: 8, color: tokens.ink2 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  const next = data.steps.find((step) => !step.doneAt)?.key;

  const actions = (key: FirstStepKey, primary: boolean): React.ReactNode => {
    const variant = primary ? 'contained' : 'outlined';
    const sx = { minHeight: 40 };
    switch (key) {
      case 'telegram':
        return <Button variant={variant} startIcon={<TelegramIcon />} onClick={() => navigate('/settings/telegram')} sx={sx}>{t('firstSteps.steps.telegram.action')}</Button>;
      case 'personalExpense':
        return (
          <>
            {telegramLinked && (
              <Button variant={variant} startIcon={<TelegramIcon />} href={`${BOT_URL}?start=gasto`} target='_blank' rel='noopener noreferrer' sx={sx}>
                {t('firstSteps.steps.personalExpense.sendToFin')}
              </Button>
            )}
            <Button variant={telegramLinked ? 'outlined' : variant} onClick={() => navigate('/transactions', { state: { newTransaction: true } })} sx={sx}>
              {t('firstSteps.steps.personalExpense.addHere')}
            </Button>
          </>
        );
      case 'budget':
        return <Button variant={variant} onClick={() => navigate('/planning', { state: { open: 'budget' } })} sx={sx}>{t('firstSteps.steps.budget.action')}</Button>;
      case 'bill':
        return <Button variant={variant} onClick={() => navigate('/planning', { state: { open: 'bill' } })} sx={sx}>{t('firstSteps.steps.bill.action')}</Button>;
      default:
        return <Button variant={variant} onClick={() => navigate('/contexts', { state: { createGroup: true } })} sx={sx}>{t('firstSteps.steps.sharedGroup.action')}</Button>;
    }
  };

  return (
    <StepList
      title={t('firstSteps.title')}
      steps={data.steps.map((step) => ({
        key: step.key,
        title: t(`firstSteps.steps.${step.key}.title`),
        hint: t(`firstSteps.steps.${step.key}.hint`),
        done: Boolean(step.doneAt),
        current: step.key === next,
        actions: actions(step.key, step.key === next),
      }))}
      aside={
        <Link
          component='button'
          type='button'
          underline='none'
          onClick={() => {
            rememberHidden(user.id, 'list');
            setHidden(true);
          }}
          sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink3 }}
        >
          {t('firstSteps.hide')}
        </Link>
      }
    />
  );
};

export default FirstStepsCard;
